import React, { useEffect, useState } from 'react';
import { array, bool, func, string, object } from 'prop-types';

import { connect } from 'react-redux';
import template from 'url-template';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { debounce, isEmpty, get, has } from 'lodash';

import FormControl from '@material-ui/core/FormControl';
import AutoComplete from '../../components/FormFields/Autocomplete';

import EsmsDialog from '../../components/EsmsDialog';

import useApi from '../../hooks/useApi';
import lambdaBaseApi from '../../apis/lambda';

import * as ArchitectActions from '../../actions/architectActions';
import * as ApplicationMessageActions from '../../actions/applicationMessageActions';
import * as SiteActions from '../../actions/siteActions';
import * as UiActions from '../../actions/uiActions';

import classes from './CreateEntityModal.module.css';

const ErpClientsUrl = template.parse('/externalclients{?agentConfigurationId,page,pageSize,name,orderby}');

const ValidationSchema = Yup.object().shape({
    agent:  Yup.string().required(),
    client: Yup.string().required()
});

const CreateClientLinkModal = ({
    close,
    clearErrors,
    open,
    title,
    isLoading,
    errors,
    toggleLoading,
    erpAgents,
    selectedNode,
    clearErpEntities,
    ...props
}) => {

    // Using the useApi hook to bypass Redux because these results do not need
    // to be part of global state, and also will be updated as per the user's
    // search term.
    const [erpClientData, getErpClientData] = useApi(searchTerm => ({
        url: ErpClientsUrl.expand({
            agentConfigurationId,
            name:     searchTerm,
            page:     1,
            pageSize: 100,
            orderby:  'name asc'
        })
    }), lambdaBaseApi);

    const [ agentConfigurationId, setAgentConfigurationId ] = useState();
    const [ erpClients, setErpClients ] = useState();
    const [ loading, setLoading ] = useState(false);

    useEffect(() => {
        if (erpClientData.data) {
            const payload = get(erpClientData, 'data', []);

            setLoading(false);

            setErpClients([
                {
                    items:     [...payload],
                    name:      'Clients',
                    itemProps: {
                        type: 'clients'
                    }
                }
            ]);
        }
    }, [erpClientData]);

    const formik = useFormik({
        initialValues: {
            agent:  '',
            client: ''
        },
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            const {
                createLink
            } = props;

            let isCreated;
            const body = {
                erpEntityKind:        'client',
                externalId:           values.client.id,
                entityId:             selectedNode.id,
                agentConfigurationId: values.agent.id
            };

            clearErrors();
            setSubmitting(true);

            try {
                if (!isLoading) {
                    toggleLoading(true);
                    isCreated = await createLink(body);
                }
                if (isCreated) {
                    setSubmitting(false);
                    toggleLoading(false);

                    handleClose();
                } else {
                    setSubmitting(false);
                    toggleLoading(false);
                }
            } catch (error) {
                setSubmitting(false);
            }
        }
    });

    const getClients = searchTerm => {
        setLoading(true);

        if (searchTerm) {
            getErpClientData(searchTerm);

            return;
        }

        getErpClientData();
    };

    const handleClose = () => {
        toggleLoading(false);
        clearErpEntities();
        clearErrors();

        close();
    };

    const handleAgentChange = event => {
        const { value } = event.target;

        setAgentConfigurationId(value.id);

        handleChange(event);
    };

    const handleChange = event => {
        const { value } = event.target;

        props.fetchErpDrilldown(value.type, value.id);

        formik.handleChange(event);
    };

    const isAutoFillArrayEmpty = array => {
        if (!array || array.length < 1) {
            return true;
        }

        if (has(array[0], 'items')) {
            return isEmpty(array[0].items);
        }

        return true;
    };

    return (
        <EsmsDialog
            errors={errors}
            open={open}
            onClose={handleClose}
            title={title}
            onRendered={formik.handleReset}
            onConfirm={formik.handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
            confirmText="Create"
        >
            <div className={classes.formColumn}>
                <div className={classes.labelColumn}>
                    <div className={classes.labelHeader}>
                        <img src="/assets/images/chain-flat.svg" alt="new area icon" />
                        <span>Select ERP Client</span>
                    </div>
                    <div className={classes.labelContent}>
                        <p>
                            A Client is representative of your Customer.
                        </p>
                        <p>
                            Select the ERP Agent where your Customer is located.
                        </p>
                        <p>
                            Select the entity in your ERP system that you'd like to link to this Client.
                        </p>
                    </div>
                </div>
                <div className={classes.inputColumn}>
                    <div className={classes.inputs}>
                        <FormControl
                            className={classes.inputWrapper}
                        >
                            <AutoComplete
                                grouped
                                label="Agent"
                                name="agent"
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                className={classes.autoComplete}
                                onChange={handleAgentChange}
                                onBlur={formik.handleBlur}
                                options={erpAgents}
                                disabled={isAutoFillArrayEmpty(erpAgents)}
                                value={formik.values.agent}
                            />
                        </FormControl>
                        <FormControl
                            className={classes.inputWrapper}
                        >
                            <AutoComplete
                                grouped
                                label="Client"
                                name="client"
                                classes={{
                                    inputRoot: classes.inputRoot
                                }}
                                className={classes.autoComplete}
                                onChange={handleChange}
                                onBlur={formik.handleBlur}
                                options={erpClients}
                                value={formik.values.client}
                                onKeyDown={debounce(getClients, 1000)}
                                loading={loading}
                            />
                        </FormControl>
                    </div>
                </div>
            </div>
        </EsmsDialog>
    );
};

CreateClientLinkModal.defaultProps = {
    clearErpEntities: () => {},
    close:            () => {},
    erpAgents:        [],
    isLoading:        false,
    open:             false,
    selectedNode:     {},
    title:            ''
};

CreateClientLinkModal.propTypes = {
    clearErpEntities: func,
    close:            func.isRequired,
    erpAgents:        array,
    isLoading:        bool,
    open:             bool.isRequired,
    selectedNode:     object,
    title:            string.isRequired
};

const mapStateToProps = state => {
    return {
        errors:       state.messages.errors,
        isLoading:    state.ui.isLoading,
        erpAgents:    state.architect.erpAgents,
        selectedNode: state.architect.selectedNode,
    };
};

const mapDispatchToProps = {
    ...ArchitectActions,
    ...ApplicationMessageActions,
    ...SiteActions,
    ...UiActions
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateClientLinkModal);
